import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';

export interface ContentBlock {
  icon: LucideIcon;
  title: string;
  body: string;
}

interface LandingPageContentSectionProps {
  heading: string;
  intro?: string;
  blocks: ContentBlock[];
  inverted?: boolean;
}

export function LandingPageContentSection({
  heading,
  intro,
  blocks,
  inverted = false,
}: LandingPageContentSectionProps) {
  return (
    <section className={`py-20 md:py-28 ${inverted ? 'bg-secondary/30' : ''}`}>
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            {heading}
          </h2>
          {intro && (
            <p className="mt-4 text-lg text-muted-foreground">{intro}</p>
          )}
        </div>
        <div className="mx-auto mt-14 grid max-w-6xl gap-6 md:grid-cols-2 lg:grid-cols-3">
          {blocks.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.article
                key={b.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="group rounded-2xl border border-border bg-card p-6 transition-all hover:border-primary/40 hover:shadow-lg"
              >
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary/20">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.body}</p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}